import { apiClient } from '@/api'
import router from '@/router'
import { useAdministrativeRegionsStore } from '@/store/modules/app/administrativeRegions'
import { useDoctorStore } from '@/store/modules/app/doctor'
import { useDoctorScheduleStore } from '@/store/modules/app/doctorSchedule'
import { useMedicalInstitutionStore } from '@/store/modules/app/medicalInstitution'
import { useMetadataStore } from '@/store/modules/app/metadata'
import { usePatientStore } from '@/store/modules/app/patient'
import { useWeComStore } from '@/store/modules/app/wecom'

export type AccountStatus = 'unknown' | 'authenticating' | 'authenticated' | 'unauthenticated'

export interface AuthenticatedAccount {
  name: string
  userid: string
}

export const useAppAccountStore = defineStore('appAccount', () => {
  const appTabbarStore = useAppTabbarStore()
  const appRouteStore = useAppRouteStore()
  const appMenuStore = useAppMenuStore()

  const status = ref<AccountStatus>('unknown')
  const account = ref('')
  const userid = ref('')
  const avatar = ref('')
  const pendingTarget = ref<string | null>(null)
  const isLoginCompleting = ref(false)
  let restorePromise: Promise<void> | undefined

  const isLogin = computed(() => status.value === 'authenticated')
  const isRestoring = computed(() => status.value === 'unknown' || status.value === 'authenticating')

  function setAuthenticated(accountInfo: AuthenticatedAccount) {
    account.value = accountInfo.name
    userid.value = accountInfo.userid
    status.value = 'authenticated'
  }

  function clearBusinessState() {
    useAdministrativeRegionsStore().reset()
    useDoctorStore().reset()
    useDoctorScheduleStore().reset()
    useMedicalInstitutionStore().reset()
    useMetadataStore().reset()
    usePatientStore().reset()
    useWeComStore().reset()
    appTabbarStore.clean()
    appRouteStore.removeRoutes()
    appMenuStore.setActived(0)
  }

  function setUnauthenticated() {
    clearBusinessState()
    account.value = ''
    userid.value = ''
    avatar.value = ''
    isLoginCompleting.value = false
    status.value = 'unauthenticated'
  }

  async function restoreCurrent() {
    if (status.value !== 'unknown') {
      return restorePromise
    }
    if (restorePromise) {
      return restorePromise
    }

    status.value = 'authenticating'
    restorePromise = (async () => {
      try {
        const current = await apiClient.account.current()
        setAuthenticated(current)
      }
      catch {
        setUnauthenticated()
      }
      finally {
        restorePromise = undefined
      }
    })()
    return restorePromise
  }

  async function login(accountInfo: AuthenticatedAccount) {
    setAuthenticated(accountInfo)
    isLoginCompleting.value = true
    return accountInfo
  }

  function completeLogin() {
    isLoginCompleting.value = false
    pendingTarget.value = null
  }

  function setPendingTarget(target: string) {
    if (target && target !== '/') {
      pendingTarget.value = target
    }
  }

  function consumePendingTarget() {
    const target = pendingTarget.value
    pendingTarget.value = null
    return target
  }

  async function requestLogout() {
    const currentPath = router.currentRoute.value.fullPath
    if (currentPath !== '/') {
      setPendingTarget(currentPath)
    }
    // Invalidate any short-lived ticket caches on implicit or explicit session loss
    try {
      const { resetTicketCache } = await import('@/api/oss-upload')
      resetTicketCache()
    }
    catch {
      // Best-effort cache cleanup without static import cycle
    }
    setUnauthenticated()
  }

  async function logout() {
    try {
      await apiClient.account.logout()
    }
    finally {
      await requestLogout()
    }
  }

  return {
    account,
    avatar,
    completeLogin,
    isLogin,
    isLoginCompleting,
    isRestoring,
    login,
    logout,
    pendingTarget,
    requestLogout,
    restoreCurrent,
    setPendingTarget,
    consumePendingTarget,
    status,
    userid,
  }
})
